import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function ProductsAPIs() {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          "https://docs.google.com/spreadsheets/d/1Lx2fvnNyuC-_OkoRt0sbH1ERbGk4NodP/gviz/tq?tqx=out:json"
        );
        const text = await res.text();
        const json = JSON.parse(text.substr(47).slice(0, -2));

        const validCols = json.table.cols
          .map((col, index) => ({ label: col.label, index }))
          .filter((col) => col.label && col.label.trim() !== "");

        const cols = validCols.map((col) => col.label);

        const rows = json.table.rows
          .map((row) =>
            validCols.map((col) => {
              const cell = row.c[col.index];
              return cell ? cell.v : "";
            })
          )
          .filter((row) => row.some((cell) => cell && cell.toString().trim() !== ""));

        setColumns(cols);
        setData(rows);
      } catch (error) {
        console.error("Error fetching sheet data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let timer;
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(timer);
      timer = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const filteredData = data.filter((row) =>
    row.some((cell) => cell.toString().toLowerCase().includes(search.toLowerCase()))
  );

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const sorted = [...filteredData].sort((a, b) => {
      const valA = a[sortConfig.key] || "";
      const valB = b[sortConfig.key] || "";
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredData, sortConfig]);

  const requestSort = (index) => {
    let direction = "asc";
    if (sortConfig.key === index && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: index, direction });
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.15 } },
  };

  const fadeInVariant = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div>
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: "url('/images/apis-banner.webp')" }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Active Pharmaceutical Ingredients (APIs)
        </h1>
      </div>

      <motion.div
        className="max-w-6xl mx-auto px-4 py-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.p className="text-lg text-gray-700 mb-6" variants={fadeInVariant}>
          At <span className="font-semibold text-[#0d47a1]">MAC LABS LTD</span>, we specialize in
          developing and manufacturing high-quality Active Pharmaceutical Ingredients that form the
          foundation of safe and effective medicines. With a strong emphasis on quality, consistency,
          and safety, our APIs serve as the building blocks for formulations that improve lives
          across the globe.
        </motion.p>

        <motion.h2 className="text-2xl font-semibold text-gray-800 mb-4" variants={fadeInVariant}>
          Our APIs Portfolio
        </motion.h2>

        <motion.div className="mb-4" variants={fadeInVariant}>
          <input
            type="text"
            placeholder="🔍 Search APIs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-1/2 p-2 border rounded-lg"
          />
        </motion.div>

        {/* Desktop Table - no horizontal scroll */}
        <div className="hidden md:block rounded-lg shadow border">
          <table className="w-full border-collapse text-sm text-left text-gray-700 table-auto break-words">
            <thead className="bg-[#0d47a1] text-white">
              <tr>
                {columns.map((col, i) => (
                  <motion.th
                    key={i}
                    className="px-4 py-2 border cursor-pointer select-none"
                    onClick={() => requestSort(i)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={false}
                  >
                    {col} {sortConfig.key === i && (sortConfig.direction === "asc" ? "▲" : "▼")}
                  </motion.th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, i) => (
                <motion.tr
                  key={i}
                  className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  whileHover={
                    !isScrolling
                      ? { scale: 1.02, boxShadow: "0px 8px 15px rgba(13,71,161,0.1)" }
                      : {}
                  }
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                >
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-2 border break-words">
                      {cell}
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {sortedData.map((row, i) => (
            <motion.div
              key={i}
              className="border rounded-lg shadow p-4 bg-white"
              whileHover={
                !isScrolling
                  ? { scale: 1.02, boxShadow: "0px 8px 15px rgba(13,71,161,0.1)" }
                  : {}
              }
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {row.map((cell, j) => (
                <p key={j} className="text-sm mb-1">
                  <span className="font-semibold text-gray-800">{columns[j]}:</span> {cell}
                </p>
              ))}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
